import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';

import { useTenantHasEnabledAssetIntegration } from '@/modules/assetProviderIntegrations/shared/hooks/useTenantHasEnabledAssetIntegration';
import { IntegrationClientForm } from '@/modules/forms/household/ManageHouseholdAccessForm/IntegrationClientForm/IntegrationClientForm';
import { SalesforceHouseholdLinkCard } from '@/modules/salesforce/SalesforceHouseholdLinkCard';

import { ClientDetailsGrantorCard } from './ClientPeopleListViewGrantorCard';
import { ClientPeopleListViewTable_ClientProfileFragment } from './graphql/ClientPeopleListViewTable.generated';

export interface ClientDetailsPrimaryClientsProps {
  primaryClients: (
    | ClientPeopleListViewTable_ClientProfileFragment
    | null
    | undefined
  )[];
  loading: boolean;
  onClick: (clientProfileId: string | null) => void;
  householdId: string;
  integrationClientIds: string[];
}

export function ClientDetailsPrimaryClients({
  primaryClients,
  loading,
  onClick,
  householdId,
  integrationClientIds,
}: ClientDetailsPrimaryClientsProps) {
  const hasAssetIntegration = useTenantHasEnabledAssetIntegration();
  const baseProps = useMemo(
    () => ({
      loading,
      onClick,
    }),
    [loading, onClick]
  );

  const cardBody = hasAssetIntegration ? (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexShrink={0}>
      <Stack
        flex={{ xs: '0 0 auto', md: '1 1 0' }}
        spacing={1}
        width={{ xs: '100%', md: 'auto' }}
        minWidth={0}
      >
        <ClientDetailsGrantorCard {...baseProps} grantor={primaryClients[0]} />
        <ClientDetailsGrantorCard {...baseProps} grantor={primaryClients[1]} />
      </Stack>
      {/*
        A grid with column auto-flow gives each rendered data-link card an
        equal-width column and stretches it to the row height. A card that
        renders null adds no column, so the other card takes the full width.
      */}
      <Box
        display="grid"
        gap={2}
        gridAutoFlow={{ xs: 'row', md: 'column' }}
        gridAutoColumns="minmax(0, 1fr)"
        flex={{ xs: '0 0 auto', md: '2 1 0' }}
        width={{ xs: '100%', md: 'auto' }}
        minWidth={0}
        role="region"
        aria-label="Data links"
      >
        <IntegrationClientForm
          householdId={householdId}
          initialIntegrationClientIds={integrationClientIds}
        />
        <SalesforceHouseholdLinkCard householdId={householdId} />
      </Box>
    </Stack>
  ) : (
    <Stack spacing={2} flexShrink={0}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box flexGrow={1}>
          <ClientDetailsGrantorCard
            {...baseProps}
            grantor={primaryClients[0]}
          />
        </Box>
        <Box flexGrow={1}>
          <ClientDetailsGrantorCard
            {...baseProps}
            grantor={primaryClients[1]}
          />
        </Box>
      </Stack>
      <SalesforceHouseholdLinkCard householdId={householdId} />
    </Stack>
  );

  return <>{cardBody}</>;
}
